import React, { useState, useCallback, useEffect } from 'react';
import { motion } from "framer-motion";
import ReactDOM from 'react-dom';

import './Modal.scss';

interface IProps {
  isShown: boolean,
  hide: () => void,
  modalContent: JSX.Element,
  className?: string
  expand?: boolean
  /** When true at close time, scroll to top instead of restoring. Caller sets ref.current = true before hide(). */
  scrollToTopOnCloseRef?: React.MutableRefObject<boolean>
}

export const useModal = () => {
  const [isShown, setIsShown] = useState<boolean>(false);

  const toggle = useCallback(() => {
    if (isShown && document.body.getElementsByClassName("Modal").length < 2) {
      document.body.style.overflow = 'unset';
    } else {
      document.body.style.overflow = 'hidden';
    }

    setIsShown(!isShown);
  }, [isShown]);

  return { isShown, toggle };
};

export const Modal: React.FC<IProps> = (props) => {
  // Set body overflow when modal opens, and clean up when it closes or unmounts
  useEffect(() => {
    if (props.isShown) {
      // Check if we have a saved scroll position
      const savedScrollPosition = sessionStorage.getItem('eventPageScrollPosition');
      const scrollY = savedScrollPosition ? parseInt(savedScrollPosition) : window.scrollY;
      
      // Clear the saved position
      sessionStorage.removeItem('eventPageScrollPosition');
      
      // Lock body scroll and save current scroll position
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      document.body.style.left = '0';
      document.body.style.right = '0';
      
      // Return cleanup function that will run when modal closes
      return () => {
        const modals = document.body.getElementsByClassName("Modal");
        
        // Only restore/scroll if there are no other modals open
        if (modals.length <= 1) {
          const scrollToTop = props.scrollToTopOnCloseRef?.current;
          if (scrollToTop) {
            props.scrollToTopOnCloseRef!.current = false;
          }

          const scrollYToRestore = document.body.style.top;

          document.body.style.position = '';
          document.body.style.top = '';
          document.body.style.width = '';
          document.body.style.overflow = 'unset';
          document.body.style.left = '';
          document.body.style.right = '';

          requestAnimationFrame(() => {
            const scrollPosition = scrollToTop ? 0 : parseInt(scrollYToRestore || '0') * -1;
            window.scrollTo({ top: scrollPosition, behavior: scrollToTop ? 'smooth' : 'auto' });
          });
        }
      };
    }
    // If modal is not shown, no cleanup needed
    return undefined;
  }, [props.isShown]);

  const modal = (
    <motion.div initial={{ opacity: 0, }} whileInView={{ opacity: 1 }} transition={{ duration: 0.1 }} className={props.className ? "Modal " + props.className: "Modal"}>
      <motion.div initial={{ opacity: 0, y: 100, }} whileInView={{ opacity: 1, y: 0 }} transition={{ type:"spring", duration: 0.3, delay: 0.25 }} className="inner" onClick={(e) => { e.stopPropagation(); }}>
        <div className="actionWrap">{props.expand && <div onClick={() => { window.location.reload(); }} className="expand">Open Event Page</div>}<div className="close" onClick={props.hide}></div></div>
        {props.modalContent}
      </motion.div>
    </motion.div>
  );

  return props.isShown ? ReactDOM.createPortal(modal, document.body) : null;
}